/**
 * ./exercise10/Exercise10Test.js
 * 
 * This module tests comprehension and memory retention after the speed reading exercise.
 */

import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../exercise5/Exercise5Test.css";
import correctIcon from '../exercise5/icons/correcticon.svg';
import wrongIcon from '../exercise5/icons/wrongicon.svg';
import { updateUserProgress } from '../utility';

const Exercise10Test = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [results, setResults] = useState({});
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const articleName = location.state?.articleName || '';
  const levelSelection = location.state?.levelSelection || 2; // ✅ Default to 2 if not found

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        const response = await fetch(`/article-text/${articleName}.csv`);
        const csvText = await response.text();
        
        // Parse CSV content
        const lines = csvText.split('\n').filter(line => line.trim());
        const dataLines = lines.slice(1); // Skip header row
        
        const parsedQuestions = dataLines.map(line => {
          const matches = line.match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g);
          if (!matches) return null;

          const [question, option1, option2, option3, option4, correctAnswer, type] = 
            matches.map(str => str.replace(/^"|"$/g, '')); // Remove surrounding quotes

          return {
            question,
            options: [option1, option2, option3, option4],
            correctAnswer: parseInt(correctAnswer.replace('Option ', '')) - 1, // Convert "Option N" to index
            type
          };
        }).filter(q => q !== null);

        setQuestions(parsedQuestions);
        setLoading(false);
      } catch (error) {
        console.error('Error loading questions:', error);
        setLoading(false);
      }
    };

    if (articleName) {
      loadQuestions();
    }
  }, [articleName]);

  const handleAnswer = (selectedOption) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = selectedOption;
    setAnswers(newAnswers);

    const newResults = { ...results };
    newResults[currentQuestion] = selectedOption === questions[currentQuestion].correctAnswer;
    setResults(newResults);

    // ✅ Wait until all questions are answered before calculating accuracy
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
      }, 1000);
    } else {
      // ✅ Final accuracy calculation after last question
      setTimeout(() => {
        let comprehensionCorrect = 0;
        let comprehensionTotal = 0;
        let memoryCorrect = 0;
        let memoryTotal = 0;

        questions.forEach((question, index) => {
          if (question.type.toLowerCase() === 'comprehension') {
            comprehensionTotal++;
            if (newResults[index]) comprehensionCorrect++;
          } else if (question.type.toLowerCase() === 'detail') {
            memoryTotal++;
            if (newResults[index]) memoryCorrect++;
          }
        });

        const comprehensionAccuracy = (comprehensionTotal > 0) 
          ? (comprehensionCorrect / comprehensionTotal) * 100 
          : 0;
        const memoryAccuracy = (memoryTotal > 0) 
          ? (memoryCorrect / memoryTotal) * 100 
          : 0;

        // ✅ Check if the user passes the criteria
        if (comprehensionAccuracy >= 75 && memoryAccuracy >= 75) {
          const durations = [2, 4, 6, 8, 10];
          updateUserProgress(10, levelSelection,0);
          if (durations.indexOf(levelSelection) === durations.length - 1){
            navigate('/exercise10fullycomplete');
          }else{
          navigate('/exercise10summary', {
            state: {
              levelSelection,
              results: newResults,
              questions,
              answers: newAnswers,
              articleName,
              comprehensionAccuracy,
              memoryAccuracy
            }
          })};
        } else {
          navigate('/exercise10failed', {
            state: {
              levelSelection,
              results: newResults,
              questions,
              answers: newAnswers,
              articleName,
              comprehensionAccuracy,
              memoryAccuracy
            }
          });
        }
      }, 1000);
    }
  };

  if (loading) {
    return <div>Loading questions...</div>;
  }

  if (!questions.length) {
    return <div>No questions found</div>;
  }

  return (
    <div className="exercise5-test-container">
      {/* Display the article text */}
      <div className="article-container">
        <h2>Book: {articleName}</h2>
        <p>Question {currentQuestion + 1} of {questions.length}</p>
        <p>Type: {questions[currentQuestion].type}</p>
      </div>

      {/* Question section */}
      <div className="question-section">
        <h1 className="question-text">{questions[currentQuestion].question}</h1>
        
        <div className="sentences-list">
          {questions[currentQuestion].options.map((option, index) => (
            <div
              key={index}
              className={`sentence-item ${answers[currentQuestion] === index ? 'selected' : ''}`}
              onClick={() => !answers[currentQuestion] && handleAnswer(index)}
            >
              {option}
              {answers[currentQuestion] === index && (
                <img 
                  src={results[currentQuestion] ? correctIcon : wrongIcon}
                  alt={results[currentQuestion] ? "Correct" : "Wrong"}
                  className="result-icon"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Exercise10Test;
